import { AdSlotType } from '../../../advertising/AdTypes';
import { WORLD_CONFIG } from '../../../config/world.config';
import { DEG_TO_RAD } from '../../../utils/math';
import { ConnectorProfile, ConnectorRole, type ConnectorDefinition } from '../../connectors/ConnectorDefinition';
import { BACKDROP_SOCKET, COLLECTIBLE_FLOAT_HEIGHT, backdropSocket, buildingSocket, collectibleLanes, nearSideSockets, standardLanes } from '../segmentBuilders';
import { SegmentKind, type DecorationSocket, type SegmentDefinition } from '../SegmentDefinition';

/**
 * Turn and intersection modules (20 m):
 *
 *            far side (closed: barrier + chevrons + ad, open: OUT)
 *        z=20 ┌──────────┐
 *    LEFT ◄── │  square  │ ──► RIGHT     square 6.6 × 6.6 m, pivot = center (0, 0, 16.7)
 *      z=16.7 │    ●     │               side exits on the square edge (x = ±3.3)
 *      z=13.4 └──────────┘
 *             │ approach │               13.4 m straight approach with ground arrows
 *         z=0 └────IN────┘
 *
 * Path length to every exit is exactly 20 m (16.7 + 3.3), so track distances
 * stay consistent whichever way the player goes.
 */
const LENGTH = WORLD_CONFIG.baseSegmentLength;
const HALF = WORLD_CONFIG.trackWidth / 2;
const PIVOT_Z = LENGTH - HALF;
const APPROACH_END = LENGTH - 2 * HALF;
const QUARTER = 90 * DEG_TO_RAD;
/**
 * Junction frequency: the user wants 4–5 junctions per km (2026-10-02). Many straight modules
 * compete for each pick (level variety phases 1–4), curves leave the track at ±45°/±90° headings
 * where fewer junction types fit, and the ad gantry (≈ every 200 m) takes plain-straight picks. So
 * once the spacing rule allows one (level.config junctions.minSegmentsBetween = 5, the rhythm), a
 * junction wins almost every pick. History: 4 → 5.2 (phase 3, ≈ 3.2/km). Measured with all modules,
 * 24 seeds × 6 km (tests/unit/helpers/trackHarness): 5.2 / minSegmentsBetween 4 → 3.0/km; now 60 / 5 →
 * 4.7/km, median gap 200 m, with 3.9 ad gantries per km (5.1 junctions/km without the gantry).
 * Regression test: tests/unit/junctionRate.test.ts.
 */
const JUNCTION_WEIGHT_SCALE = 60;
/** A closed end always shows a shell building straight ahead (front 4.5 m behind the far barrier). */
const FAR_BACKDROP_Z = LENGTH + 11.5;
/** BLD_SHELL_A: scaffold front 7.03 m in front of its origin (BLD_Shell_A.py). */
const SHELL_SCAFFOLD_FRONT = 7.03;
/**
 * Large scaffold wrap on that building (user: size of the red frame, 2026-10-01): 10.08 x 7.2 m, aspect 1.4.
 * gap: in front of the scaffold's outer toe boards and edges (z 7.03) – 3 cm flickered from ~150 m
 * (z-fighting, 2026-10-02); 8 cm like the fence banners, plus the ads' depth offset.
 */
const BUILDING_WRAP = { width: 10.08, height: 7.2, bottom: 5.0, gap: 0.08 } as const;

interface JunctionSpec {
  readonly id: string;
  readonly kind: SegmentKind;
  readonly displayName: string;
  readonly left: boolean;
  readonly right: boolean;
  readonly straight: boolean;
  readonly weight: number;
}

const exit = (role: ConnectorDefinition['role'], x: number, z: number, yaw: number): ConnectorDefinition => ({
  id: `CONNECTOR_${role}`,
  role,
  profile: ConnectorProfile.Ground3Lane,
  transform: { x, y: 0, z, yaw },
  allowedNextProfiles: [ConnectorProfile.Ground3Lane],
});

function createJunction(spec: JunctionSpec): SegmentDefinition {
  const exits: ConnectorDefinition[] = [];
  if (spec.left) exits.push(exit(ConnectorRole.Left, -HALF, PIVOT_Z, -QUARTER));
  if (spec.straight) exits.push(exit(ConnectorRole.Out, 0, LENGTH, 0));
  if (spec.right) exits.push(exit(ConnectorRole.Right, HALF, PIVOT_Z, QUARTER));

  // Decoration only on a fully closed side – open sides belong to the exit modules. A closed side
  // carries two roadside bays like a straight (z 0–20, clear of the closed-end building at z ≥ 24.5).
  const decorationSockets: DecorationSocket[] = [];
  if (!spec.left) decorationSockets.push(...nearSideSockets('LEFT', '', 0));
  if (!spec.right) decorationSockets.push(...nearSideSockets('RIGHT', '', 0));
  // Backdrop on the closed sides; spacing at runtime keeps it clear of the branch backdrops.
  if (!spec.left) decorationSockets.push(backdropSocket('SOCKET_DECO_L_04', 'LEFT', -BACKDROP_SOCKET.x, LENGTH / 2, QUARTER));
  if (!spec.right) decorationSockets.push(backdropSocket('SOCKET_DECO_R_04', 'RIGHT', BACKDROP_SOCKET.x, LENGTH / 2, -QUARTER));
  if (!spec.straight) decorationSockets.push(buildingSocket('SOCKET_DECO_F_04', 'FAR', 0, FAR_BACKDROP_Z, Math.PI));

  return {
    id: spec.id,
    kind: spec.kind,
    displayName: spec.displayName,
    length: LENGTH,
    width: WORLD_CONFIG.trackWidth,
    visualAssetId: spec.id,
    entry: {
      id: 'CONNECTOR_IN',
      role: ConnectorRole.In,
      profile: ConnectorProfile.Ground3Lane,
      transform: { x: 0, y: 0, z: 0, yaw: 0 },
      allowedNextProfiles: [],
    },
    exits,
    lanes: standardLanes(WORLD_CONFIG),
    obstacleSpawnPoints: [],
    obstacleRowLayouts: { sparse: [], dense: [] },
    collectibleSpawnPoints: collectibleLanes(WORLD_CONFIG, 1, APPROACH_END - 2.4, 2, COLLECTIBLE_FLOAT_HEIGHT),
    decorationSockets,
    dynamicEventSockets: [],
    // A closed far side is the most visible spot of the whole module – prime ad space.
    advertisingSlots: spec.straight
      ? []
      : [
          {
            id: 'AD_BUILDING_WRAP',
            slotType: AdSlotType.BuildingWrap,
            // on the scaffold of the shell building that SOCKET_DECO_F_04 always carries
            position: {
              x: 0,
              y: BUILDING_WRAP.bottom + BUILDING_WRAP.height / 2,
              z: FAR_BACKDROP_Z - SHELL_SCAFFOLD_FRONT - BUILDING_WRAP.gap,
            },
            yaw: Math.PI,
            width: BUILDING_WRAP.width,
            height: BUILDING_WRAP.height,
            fillRate: 1, // the most visible spot of the module always carries the ad
          },
        ],
    junction: { pivot: { x: 0, y: 0, z: PIVOT_Z } },
    generation: {
      weight: spec.weight,
      minDistance: 150,
      // the ructec-Bulli drives through crossings (straight on or turning) – no forced turns or T-junctions in its ride
      tags: ['junction', spec.kind.toLowerCase(), ...(spec.kind === SegmentKind.Crossing ? ['bus-road'] : [])],
    },
  };
}

export const SEG_TURN_LEFT_20M = createJunction({
  id: 'SEG_TURN_LEFT_20M',
  kind: SegmentKind.TurnLeft,
  displayName: 'Abzweigung links 20 m',
  left: true,
  right: false,
  straight: false,
  weight: 0.14 * JUNCTION_WEIGHT_SCALE,
});

export const SEG_TURN_RIGHT_20M = createJunction({
  id: 'SEG_TURN_RIGHT_20M',
  kind: SegmentKind.TurnRight,
  displayName: 'Abzweigung rechts 20 m',
  left: false,
  right: true,
  straight: false,
  weight: 0.14 * JUNCTION_WEIGHT_SCALE,
});

export const SEG_JUNCTION_T_20M = createJunction({
  id: 'SEG_JUNCTION_T_20M',
  kind: SegmentKind.TJunction,
  displayName: 'T-Kreuzung 20 m',
  left: true,
  right: true,
  straight: false,
  weight: 0.08 * JUNCTION_WEIGHT_SCALE,
});

export const SEG_CROSSING_20M = createJunction({
  id: 'SEG_CROSSING_20M',
  kind: SegmentKind.Crossing,
  displayName: 'Kreuzung 20 m',
  left: true,
  right: true,
  straight: true,
  weight: 0.08 * JUNCTION_WEIGHT_SCALE,
});

export const JUNCTION_SEGMENTS: readonly SegmentDefinition[] = [
  SEG_TURN_LEFT_20M,
  SEG_TURN_RIGHT_20M,
  SEG_JUNCTION_T_20M,
  SEG_CROSSING_20M,
];
